import type {
    TFetchCurrentUserOrgInstructors,
    TAddInstructor,
    TUpdateInstructor,
    TCancelInstructorInvite,
    TArchiveInstructor,
    TActivateInstructor,
} from '@/store/instructor/types'
import type { Teach, Study } from '@pocketprep/types'
import { Parse, runCloudFunction } from '@/store/ParseUtils'
import { fetchLibLoadable, fetchLoadable, resetLoadable } from '@/loadingUtils'
import { instructorModule } from '@/store/instructor/module'
import { groupModule } from '@/store/group/module'
import { organizationModule } from '@/store/organization/module'
import { userModule } from '@/store/user/module'

const archiveInstructor = async (params: { instructorId: string }) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('archiveInstructor: Could not find currentOrganization')
    }

    await runCloudFunction<TArchiveInstructor>('archiveInstructor', params)

    resetLoadable(instructorModule.state.currentOrgInstructors[currentOrganization.objectId])

    await fetchCurrentOrgInstructors()
}

const activateInstructor = async (params: { instructorId: string; organizationId?: string }) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()
    const orgId = params.organizationId || currentOrganization?.objectId

    if (!orgId) {
        throw new Error('activateInstructor: Could not find currentOrganization')
    }

    await runCloudFunction<TActivateInstructor>('activateInstructor',
        { ...params, organizationId: orgId }
    )

    if (params.organizationId) {
        const userOrgsInstructors = instructorModule.getters.getCurrentUserOrgInstructors()
        const newOrg = userOrgsInstructors.find(uoi =>
            uoi.organization.objectId === params.organizationId)?.organization as Teach.Class.OrganizationJSON
        if (newOrg) {
            userModule.actions.updateCurrentOrganization(newOrg)
        }
    }

    resetLoadable(instructorModule.state.currentUserOrgInstructors)

    await fetchCurrentUserOrgInstructors()
}

const cancelInstructorInvite = async (params: { instructorId: string }) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('cancelInstructorInvite: Could not find currentOrganization')
    }

    await runCloudFunction<TCancelInstructorInvite>('cancelInstructorInvite', params)

    resetLoadable(instructorModule.state.currentOrgInstructors[currentOrganization.objectId])
    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])

    await Promise.all([
        fetchCurrentOrgInstructors(),
        groupModule.actions.fetchCurrentOrgGroups(),
    ])
}

const updateInstructor = async (params: {
    email?: string
    customMessage?: string
    instructorId: string
    accountType: 'admin' | 'instructor'
    hasBillingAccess: boolean
    orgExamIds: string[]
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('updateInstructor: Could not find currentOrganization')
    }

    await runCloudFunction<TUpdateInstructor>('updateInstructor', params)

    resetLoadable(instructorModule.state.currentOrgInstructors[currentOrganization.objectId])
    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])

    await Promise.all([
        fetchCurrentOrgInstructors(),
        groupModule.actions.fetchCurrentOrgGroups(),
    ])
}

const addInstructor = async (params: {
    organizationId: string
    email: string
    customMessage: string
    orgExamIds: string[]
    accountType: 'admin' | 'instructor'
    hasBillingAccess: boolean
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('addInstructor: Could not find currentOrganization')
    }

    await runCloudFunction<TAddInstructor>('addInstructor', params)

    resetLoadable(instructorModule.state.currentOrgInstructors[currentOrganization.objectId])
    resetLoadable(instructorModule.state.currentOrgInstructorUsers[currentOrganization.objectId])
    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])

    await Promise.all([
        fetchCurrentOrgInstructors(),
        fetchCurrentOrgInstructorUsers(),
        groupModule.actions.fetchCurrentOrgGroups(),
    ])
}

const fetchCurrentOrgInstructorUsers = async () => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('fetchCurrentOrgInstructorUsers: Could not find currentOrganization')
    }

    const currentInstructors = await fetchCurrentOrgInstructors()

    return fetchLibLoadable(instructorModule, 'currentOrgInstructorUsers', currentOrganization.objectId, async () => {
        type TFetchUsers = (params: { userIds: string[] }) => Promise<Study.Class.UserJSON[]>
        const instructorUsers: Study.Class.UserJSON[] = await runCloudFunction<TFetchUsers>(
            'fetchInstructorUsers',
            { userIds: Object.values(currentInstructors).map(s => s.user.objectId) }
        )

        return instructorUsers.reduce((acc, s) => ({
            ...acc,
            [s.objectId]: s,
        }), {} as { [key: string]: Study.Class.UserJSON })
    })
}

const updateVisibleOrgGroups = async (orgGroupIds: string[]) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()
    if (!currentOrganization) {
        throw new Error('updateVisibleOrgGroups: Could not find currentOrganization')
    }

    const orgInstructor = await fetchCurrentOrgInstructor()
    if (!orgInstructor) {
        throw new Error('updateVisibleOrgGroups: Could not find current user')
    }

    type TParseUpdateVisibleOrgGroups =
        (params: { orgInstructorId: string; orgGroupIds: string[] }) => Promise<void>
    await runCloudFunction<TParseUpdateVisibleOrgGroups>(
        'updateVisibleOrgGroups',
        { orgGroupIds, orgInstructorId: orgInstructor.objectId }
    )

    await fetchCurrentOrgInstructors({ forceFetch: true })
}

const fetchCurrentOrgInstructors = async (options?: { forceFetch?: boolean }) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()
    if (!currentOrganization || !Parse.User.current()) {
        return {}
    }

    return fetchLibLoadable(instructorModule, 'currentOrgInstructors', currentOrganization.objectId, async () => {
        type TFetchOrgInstructors = (params: { organizationId: string }) => Teach.Class.OrgInstructorJSON[]
        const instructors = await runCloudFunction<TFetchOrgInstructors>(
            'fetchOrgInstructors',
            { organizationId: currentOrganization.objectId }
        )
        return instructors.reduce(
            (acc, i) => ({ ...acc, [i.objectId]: i }),
            {} as { [key: string]: Teach.Class.OrgInstructorJSON }
        )
    }, options?.forceFetch)
}

const fetchCurrentOrgInstructor = async () => {
    const currentUser = Parse.User.current<Study.Class.User>()
    if (!currentUser) {
        return null
    }

    const orgInstructors = await fetchCurrentOrgInstructors()

    const currentOrgInstructor = Object.values(orgInstructors)
        .find(orgInstructor => orgInstructor.user.objectId === currentUser.id)

    if (!currentOrgInstructor) {
        return null
    }

    if (instructorModule.state.currentOrgInstructorId !== currentOrgInstructor.objectId) {
        instructorModule.state.currentOrgInstructorId = currentOrgInstructor.objectId
    }

    return instructorModule.getters.getCurrentOrgInstructor()
}

const fetchCurrentUserOrgInstructors = async () => {
    const currentUser = Parse.User.current<Study.Class.User>()
    if (!currentUser) {
        return []
    }

    return fetchLoadable(instructorModule.state.currentUserOrgInstructors,
        async () => runCloudFunction<TFetchCurrentUserOrgInstructors>('fetchCurrentUserOrgInstructors'))
}

export default {
    archiveInstructor,
    cancelInstructorInvite,
    updateInstructor,
    addInstructor,
    updateVisibleOrgGroups,
    fetchCurrentUserOrgInstructors,
    fetchCurrentOrgInstructor,
    fetchCurrentOrgInstructors,
    fetchCurrentOrgInstructorUsers,
    activateInstructor,
}
